import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_DOMAIN = 'https://www.onpoint.works';
const SITE_NAME = 'OnPoint WFM Analytics Lab';

// Environment-aware indexing - check current hostname against production domain
const isProduction = () => {
  // In browser context, check actual hostname
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    return currentHost === 'www.onpoint.works' || currentHost === 'onpoint.works';
  }
  return false;
};

// Page-specific SEO data
const PAGE_SEO = {
  '/': {
    title: 'OnPoint WFM Analytics Lab | Structural Distortion Classification',
    description: 'File-based workforce analytics engagement. Structural distortion classification for CX workforce systems using exported interval-level data.',
  },
  '/about': {
    title: 'OnPoint WFM Analytics Lab',
    description: 'Learn about OnPoint.Works WFM Analytics Lab. Analytical interpretation services for workforce management systems operated by Avyria LLC.',
  },
  '/atlas': {
    title: 'Atlas | OnPoint WFM Analytics Lab',
    description: 'The OnPoint Atlas: comprehensive structural classification framework for workforce management distortion patterns.',
  },
  '/services': {
    title: 'Services | OnPoint WFM Analytics Lab',
    description: 'WFM analytics services overview. Structural classification, diagnostic memoranda, and analytical interpretation for workforce systems.',
  },
  '/rapid-diagnostic': {
    title: 'OnPoint Diagnostics | OnPoint WFM Analytics Lab',
    description: 'Three-week OnPoint Diagnostics engagement for workforce management systems. Structural distortion classification with executive memorandum output.',
  },
  '/demo': {
    title: 'Demo Artifacts | OnPoint WFM Analytics Lab',
    description: 'Example deliverables and demonstration artifacts from OnPoint WFM Analytics Lab engagements.',
  },
  '/intake': {
    title: 'Begin Intake | OnPoint WFM Analytics Lab',
    description: 'Start your workforce analytics engagement. Submit interval-level export data for structural distortion classification.',
  },
  '/diagnostic-preview': {
    title: 'Diagnostic Preview | OnPoint WFM Analytics Lab',
    description: 'Preview diagnostic output structure and deliverable formats for workforce analytics engagements.',
  },
  '/procurement': {
    title: 'Procurement Ready | OnPoint WFM Analytics Lab',
    description: 'Procurement documentation and vendor qualification materials for enterprise workforce analytics engagements.',
  },
  '/faq': {
    title: 'FAQ & Boundaries | OnPoint WFM Analytics Lab',
    description: 'Frequently asked questions about engagement scope, boundaries, and analytical methodology.',
  },
  '/contact': {
    title: 'Contact | OnPoint WFM Analytics Lab',
    description: 'Contact OnPoint.Works WFM Analytics Lab. Operated by Avyria LLC, New Mexico, USA.',
  },
  '/exports': {
    title: 'Export Specifications | OnPoint WFM Analytics Lab',
    description: 'Technical specifications for data exports. Supported formats and interval-level data requirements.',
  },
  '/sample-memo': {
    title: 'Sample Memo | OnPoint WFM Analytics Lab',
    description: 'Sample executive memorandum format demonstrating diagnostic deliverable structure and analytical output.',
  },
  '/delivery-model': {
    title: 'Delivery Model | OnPoint WFM Analytics Lab',
    description: 'Engagement delivery model and timeline. File-based analytical process without system access.',
  },
  '/legal': {
    title: 'Legal & Data Handling | OnPoint WFM Analytics Lab',
    description: 'Legal framework, data handling policies, and retention terms for workforce analytics engagements.',
  },
  '/thank-you': {
    title: 'Thank You | OnPoint WFM Analytics Lab',
    description: 'Engagement submission confirmed. Next steps for your workforce analytics diagnostic.',
  },
  '/auth': {
    title: 'Authentication | OnPoint WFM Analytics Lab',
    description: 'Secure access portal for OnPoint WFM Analytics Lab.',
  },
  '/intake/success': {
    title: 'Protocol Initiated | OnPoint WFM Analytics Lab',
    description: 'Your intake and payment have been received. We will confirm your Pre-Diagnostics Call slot by email.',
  },
  '/intake/cancelled': {
    title: 'Payment Cancelled | OnPoint WFM Analytics Lab',
    description: 'Payment was cancelled. Return to intake form to try again.',
  },
};

export function SEOHead({ title, description }) {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  
  // Get page-specific SEO or use provided/default values
  const pageSeo = PAGE_SEO[pathname] || {};
  const pageTitle = title || pageSeo.title || SITE_NAME;
  const pageDescription = description || pageSeo.description || 'Structural distortion classification for CX workforce systems.';
  
  // Canonical URL - always points to production domain, lowercase, no trailing slash
  const canonicalUrl = `${CANONICAL_DOMAIN}${pathname.toLowerCase()}`;
  
  // Determine robots directive
  const shouldNoIndex = !isProduction();

  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }
    
    // Update canonical link
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    }
    
    // Update robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', shouldNoIndex ? 'noindex,nofollow' : 'index,follow');
    
    // Update OG tags
    const updateMeta = (property, content) => {
      const meta = document.querySelector(`meta[property="${property}"]`);
      if (meta) meta.setAttribute('content', content);
    };
    
    updateMeta('og:title', pageTitle);
    updateMeta('og:description', pageDescription);
    updateMeta('og:url', canonicalUrl);
    
    // Update Twitter tags
    const updateTwitterMeta = (name, content) => {
      const meta = document.querySelector(`meta[name="${name}"]`);
      if (meta) meta.setAttribute('content', content);
    };
    
    updateTwitterMeta('twitter:title', pageTitle);
    updateTwitterMeta('twitter:description', pageDescription);
    
  }, [pageTitle, pageDescription, canonicalUrl, shouldNoIndex]);

  return null;
}
